import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { buildTheme } from './design-system/theme'
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext'
import { CssBaseline } from '@mui/material'
import ErrorBoundary from './components/ErrorBoundary'
import SafeComponent from './components/SafeComponent'
import ProtectedRoute from './components/auth/ProtectedRoute'
import RequireGlobalAdmin from './components/auth/RequireGlobalAdmin'
import { AuthProvider } from './contexts/AuthContext'
import { OrgProvider } from './contexts/OrgContext'
import { ProjectProvider, useProject } from './contexts/ProjectContext'
import { useAuth } from './contexts/AuthContext'
import { projectAPI } from './services/projectAPI'
import { setupGlobalErrorHandlers, setupDevHelpers } from './utils/errorHandler'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import { ToastProvider, useToast } from './contexts/ToastContext'
import aiConsentService from './services/aiConsentService'
import { summarizeIntentAsProjectName } from './pages/AutoInsight/AIBuild/aiBuildCommonPageUtils'
import { DATA_ROUTES } from './navigation/consoleRoutes'
import { buildMarketingRoutes } from './navigation/marketingRoutes'
import { featureFlags } from './config/featureFlags'
import './index.css'

// 글로벌 에러 핸들러 및 개발 도구 설정
setupGlobalErrorHandlers()
setupDevHelpers()

// VC-G11: 홍보 축(landing) vs 서비스 축(app shell) 분리.
// ADR-018 W3: 마케팅/랜딩 라우트(`/`, `/app`, `/platform`, `/pricing`, `/docs`,
// `/contact`, `/ready-made-packs`)는 `buildMarketingRoutes()` 로 이동하여
// `VITE_EDITION=onprem` 빌드에서 통째로 제외된다. lazy import 도 그쪽에서
// 게이팅되므로 on-prem 번들은 해당 청크를 드롭한다.
const AppLayout = React.lazy(() => import('./components/layout/AppLayout'))
const ProjectManagement = React.lazy(
  () => import('./pages/ProjectManagement/ProjectManagement')
)
// Phase F (2026-06-03): Use Cases catalog page (Project > Use Cases sidebar entry).
const UseCasesPage = React.lazy(
  () => import('./pages/ProjectManagement/UseCasesPage')
)
const OntologyManagement = React.lazy(
  () => import('./pages/OntologyManagement')
)
const DataManagement = React.lazy(() => import('./pages/DataManagement'))
const SparqlQuery = React.lazy(() => import('./pages/SparqlQuery'))
// 2026-05-31 IA Round 2 Wave 2A: console surface가 3 URL prefix로 분리.
// 각 페이지는 ConsoleSurface에 section prop만 넘기는 thin wrapper.
const AutoInsightPage = React.lazy(
  () => import('./pages/AutoInsight/AutoInsightPage')
)
const DesignerPage = React.lazy(() => import('./pages/Designer/DesignerPage'))
const SecretManagementPage = React.lazy(
  () => import('./pages/Designer/SecretManagement')
)
// AI Build 는 4-surface (pack / workflow / ontology / report) 통합 설계자라
// Designer 하위가 아닌 Project 진입점에 둔다 (2026-06-07 IA 정합).
const AIBuildPage = React.lazy(
  () => import('./pages/AutoInsight/AIBuild/AIBuildPage')
)
const OperatePage = React.lazy(() => import('./pages/Operate/OperatePage'))
const SettingsPage = React.lazy(() => import('./pages/Settings/SettingsPage'))
const UserApprovalPage = React.lazy(
  () => import('./pages/Settings/UserApprovalPage')
)
const LayerRouteShell = React.lazy(
  () => import('./components/layout/LayerRouteShell')
)
// Designer and Operate are current top-level console surfaces.
const ObjectLineageFullView = React.lazy(
  () => import('./pages/AutoInsight/LineageExplorer/ObjectLineageFullView')
)
// VC-G6: Governance hub 신규 entry
const GovernanceHubPage = React.lazy(
  () => import('./pages/Governance/GovernanceHubPage')
)
const PolicyConsole = React.lazy(
  () => import('./pages/Governance/PolicyConsole')
)
const EvidenceBrowser = React.lazy(
  () => import('./pages/Governance/EvidenceBrowser')
)
const ObservabilityDashboard = React.lazy(
  () => import('./pages/Governance/ObservabilityDashboard')
)
const JourneyRegressionDashboard = React.lazy(
  () => import('./pages/Governance/JourneyRegressionDashboard')
)
// WT_008: dedicated route for the Trino federated-query connector form.
const TrinoConnectorPage = React.lazy(
  () => import('./pages/Data/Connectors/TrinoConnectorPage')
)
// VC-G7: Design system showcase
const DesignSystemPage = React.lazy(
  () => import('./pages/DesignSystem/DesignSystemPage')
)
// Marketing sub-pages (홍보 축) 는 ADR-018 W3 에 따라 navigation/marketingRoutes
// 로 이동 — VITE_EDITION 게이팅 + 청크 드롭. 여기서는 buildMarketingRoutes() 만
// 사용한다.
// VC-AGENT-EXT-04: Agent template marketplace (console 내부 catalog placeholder + 3 공식 template seed)
const AgentMarketplacePage = React.lazy(
  () => import('./pages/AgentMarketplace/AgentMarketplacePage')
)
// 2026-05-31 IA Round 3 Wave R4: catch-all 404 페이지
const NotFoundPage = React.lazy(() => import('./pages/NotFound/NotFoundPage'))
// Phase 1 / P4 (2026-06-03): Org introduction routes.
const WelcomePage = React.lazy(() => import('./pages/Welcome/WelcomePage'))
const OrgSettingsPage = React.lazy(
  () => import('./pages/Organizations/OrgSettingsPage')
)
const AllProjectsPage = React.lazy(
  () => import('./pages/ProjectManagement/AllProjectsPage')
)
// RMPA Phase C — admin-only Ready Made Pack authoring console.
const PackAuthoringPage = React.lazy(
  () => import('./pages/Admin/ReadyMadePackAuthoring/PackAuthoringPage')
)
const PackAuthoringEditorPage = React.lazy(
  () => import('./pages/Admin/ReadyMadePackAuthoring/PackAuthoringEditorPage')
)
// RMPA polish W6: AdminLayout wraps /admin/* routes with a left rail
// SideNav + header so operators have navigation back to the main
// console. Without this shell admin pages rendered with no chrome.
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'))
// Phase 2 follow-up Sprint 3 / S3.A — Operator console at /app/ops.
// AdminGate inside OpsPage redirects non-global-admins to /projects with
// a toast. Backend additionally returns 403 on /api/admin/ops/* for any
// non-admin caller.
const OpsPage = React.lazy(() => import('./pages/Admin/Ops/OpsPage'))

// VC-G4: ThemeModeProvider 의 effectiveMode (light|dark) 에 따라 reactive 하게 MUI theme 빌드.
const AppThemeBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { effectiveMode } = useThemeMode()
  const theme = React.useMemo(() => {
    const base = buildTheme(effectiveMode)
    return {
      ...base,
      // zIndex.drawer override 유지 (sidebar above modal default)
      zIndex: { ...base.zIndex, drawer: 1200 },
    }
  }, [effectiveMode])
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}

// /use-cases drops users into the in-app Use Case project. /use-cases/:caseId
// selects the scenario via query param so showcase navigation hooks pick it up.
function UseCaseEntryRedirect() {
  return <Navigate to="/app?project=project-use-case" replace />
}

function UseCaseScenarioRedirect() {
  const { caseId } = useParams()
  const target = caseId
    ? `/app?project=project-use-case&scenario=${encodeURIComponent(caseId)}`
    : '/app?project=project-use-case'
  return <Navigate to={target} replace />
}

// AI Build 가 통합 설계자로 Project 영역에 standalone route 로 마운트되는
// 곳에서 `useProject()` 의 currentProject 를 prop 으로 주입하는 얇은 wrapper.
// ConsoleSurface 와 동일한 projectId 주입 패턴을 standalone route 에도 적용.
//
// 2026-06-14 — 0→1 bootstrap. 빈 워크스페이스 사용자가 AI Build 에 들어와도
// dead-end 가 되지 않게 `onBootstrapProjectFromIntent` 를 주입한다.
// 사용자가 Run 을 누르면 intent 첫 문장을 이름으로 하는 새 프로젝트를 만들고
// setCurrentProject 로 컨텍스트를 갈아끼운 뒤 그 새 projectId 를 controller 에
// 넘겨 planner orchestration 이 곧바로 실행되도록 한다.
function ProjectsAIBuildRoute() {
  const { currentProject, setCurrentProject, setProjects } = useProject()
  const { currentUser, userProfile } = useAuth()
  const toast = useToast()
  // R017 E1 — rename callback 이 async run 중에도 최신 currentProject 를
  // 보도록 ref 로 추적 (controller 는 run 시작 시점의 prop 을 붙잡고 있음).
  const currentProjectRef = React.useRef(currentProject)
  React.useEffect(() => {
    currentProjectRef.current = currentProject
  }, [currentProject])

  const handleBootstrapProjectFromIntent = React.useCallback(
    async (rawIntent: string): Promise<string | null> => {
      const callerEmail = currentUser?.email || userProfile?.email
      if (!callerEmail) return null

      // R017 E1 — intent 전문(첫 줄 40자 slice) 대신 명사구 요약을 초기
      // 이름으로 쓴다. planner orchestration 이 끝나면 proposal metadata 의
      // title 로 다시 갱신된다 (handleRenameBootstrappedProject).
      const name =
        summarizeIntentAsProjectName(rawIntent) ||
        `AI Build 프로젝트 ${new Date().toISOString().slice(0, 10)}`
      const description = rawIntent.trim().slice(0, 280) || name

      try {
        const created = await projectAPI.createProject({
          id: name,
          name,
          description,
          createdAt: new Date().toISOString().split('T')[0],
          updatedAt: new Date().toISOString().split('T')[0],
          owner: callerEmail,
          members: [],
          ontologyClasses: 0,
          dataEntries: 0,
          sparqlQueries: 0,
        })
        if (!created) return null
        setProjects((prev) => [...prev, created])
        setCurrentProject(created)
        // 2026-06-14 — 사용자가 Run 버튼을 누른 행위 자체가 "이 프로젝트에서
        // AI 처리를 시작해도 좋다" 는 명시적 consent 다. AIConsentGate 가
        // setCurrentProject 직후 새 projectId 로 fetch 할 때 다이얼로그를
        // 띄우는 race 를 피하려면 backend POST 가 완료되기 전이라도
        // localStorage 에 granted 를 박아둬야 한다 — grant() 가 양쪽 다
        // 처리해주므로 await.
        await aiConsentService.grant(created.id)
        // 사용자에게 "방금 silent 하게 무슨 일이 일어났는지" 명시.
        // ProjectChip 이 갑자기 등장하기 직전에 toast 가 뜨면 "프로젝트가
        // 만들어졌구나" 를 즉각 인지할 수 있다.
        toast.showSuccess(`'${created.name}' 프로젝트가 생성되었습니다`)
        return created.id
      } catch (err) {
        console.error('AI Build bootstrap project 실패:', err)
        return null
      }
    },
    [
      currentUser?.email,
      userProfile?.email,
      setProjects,
      setCurrentProject,
      toast,
    ]
  )

  // R017 E1 — planner orchestration 완료 후 proposal metadata title 로
  // bootstrap 프로젝트명을 갱신. 실패는 controller 쪽에서 무시된다.
  const handleRenameBootstrappedProject = React.useCallback(
    async (bootstrappedProjectId: string, name: string): Promise<void> => {
      const trimmed = name.trim()
      if (!trimmed) return
      await projectAPI.updateProject(bootstrappedProjectId, { name: trimmed })
      setProjects((prev) =>
        prev.map((p) =>
          p.id === bootstrappedProjectId ? { ...p, name: trimmed } : p
        )
      )
      const active = currentProjectRef.current
      if (active && active.id === bootstrappedProjectId) {
        setCurrentProject({ ...active, name: trimmed })
      }
    },
    [setProjects, setCurrentProject]
  )

  return (
    <AIBuildPage
      projectId={currentProject?.id ?? null}
      onBootstrapProjectFromIntent={handleBootstrapProjectFromIntent}
      onRenameBootstrappedProject={handleRenameBootstrappedProject}
    />
  )
}

function App() {
  const renderLayerManagedRoute = (
    child: React.ReactNode,
    loadingText: string
  ) => (
    <SafeComponent loadingText={loadingText}>
      <LayerRouteShell>{child}</LayerRouteShell>
    </SafeComponent>
  )

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <AuthProvider>
          <Router>
            {/* Phase 1 / P4: OrgProvider must sit inside Router (uses
                useNavigate for switch redirect) and wrap ProjectProvider so
                ProjectContext can consume current-org scope in a follow-up
                without re-shuffling providers. */}
            <OrgProvider>
              <ProjectProvider>
                <ThemeModeProvider>
                  <AppThemeBridge>
                    <ToastProvider>
                      <SafeComponent loadingText="앱 셸 로딩 중...">
                        <AppLayout>
                          <Routes>
                            {/* VC-G11 + ADR-018 W3: `/` = landing, `/app` =
                              service shell, 그리고 마케팅 sub-pages 는 모두
                              buildMarketingRoutes() 가 반환한다. SaaS 빌드에서는
                              전부 유지되고, VITE_EDITION=onprem 빌드에서는 `/`·
                              `/app` 이 앱(`/projects`)으로 redirect 되고 나머지
                              마케팅 라우트는 제외된다. */}
                            {buildMarketingRoutes()}
                            {/* T013 (2026-06-07): `/login` 직접 진입은 SPA 에
                              라우트가 없어 catch-all 로 떨어져 404 였다. 로그인
                              은 home 의 모달이므로 `/` 로 redirect 한다. 향후
                              `/login` query/state 를 home 이 읽어 모달을 자동
                              오픈하는 것을 권장하나, 이번 batch 는 최소 fix
                              으로 단순 Navigate 만 적용. */}
                            <Route
                              path="/login"
                              element={<Navigate to="/" replace />}
                            />
                            {/* Phase 2 follow-up S3.A — Operator console (/app/ops).
                              Global-admin only. Phase D (2026-06-07 / TASK_004):
                              route-level `RequireGlobalAdmin` guard handles the
                              redirect + Snackbar before the page mounts. The
                              page-internal AdminGate stays as a redundant
                              safety net for backwards compatibility. */}
                            <Route
                              path="/app/ops"
                              element={
                                <RequireGlobalAdmin>
                                  <SafeComponent loadingText="운영자 콘솔 로딩 중...">
                                    <OpsPage />
                                  </SafeComponent>
                                </RequireGlobalAdmin>
                              }
                            />
                            {/* Marketing sub-pages (/platform, /ready-made-packs,
                              /pricing, /docs, /contact) 는 buildMarketingRoutes()
                              위에서 등록됨 (ADR-018 W3). */}
                            {/* /use-cases selects a scenario inside the showcase
                              project — 앱 진입 redirect 이므로 마케팅 제외 대상이
                              아니다 (모든 에디션 유지). */}
                            <Route
                              path="/use-cases"
                              element={<UseCaseEntryRedirect />}
                            />
                            <Route
                              path="/use-cases/:caseId"
                              element={<UseCaseScenarioRedirect />}
                            />
                            <Route
                              path="/projects"
                              element={
                                <SafeComponent loadingText="프로젝트 관리 로딩 중...">
                                  <ProjectManagement />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/projects/ready-made-packs"
                              element={
                                <SafeComponent loadingText="Ready Made Packs 로딩 중...">
                                  <ProjectManagement />
                                </SafeComponent>
                              }
                            />
                            {/* Phase F (2026-06-03): Use Cases catalog
                              (`/api/use-cases` 카탈로그 + subscribe gate). */}
                            <Route
                              path="/projects/use-cases"
                              element={
                                <SafeComponent loadingText="Use Cases 로딩 중...">
                                  <UseCasesPage />
                                </SafeComponent>
                              }
                            />
                            {/* /projects/onboarding 옛 라우트는 AI Build 가
                              진입 가이드 역할을 흡수하므로 redirect 한다. */}
                            <Route
                              path="/projects/onboarding"
                              element={
                                <Navigate to="/projects/ai-build" replace />
                              }
                            />
                            {/* 2026-06-07 IA — AI Build 는 통합 설계자라
                              Project 진입점으로 이동. 옛 경로 /designer/ai-build
                              는 아래 catch-all 보다 먼저 정의된 redirect 가 처리. */}
                            <Route
                              path="/projects/ai-build"
                              element={renderLayerManagedRoute(
                                <ProjectsAIBuildRoute />,
                                'AI Build 로딩 중...'
                              )}
                            />
                            <Route
                              path="/designer/ai-build"
                              element={
                                <Navigate to="/projects/ai-build" replace />
                              }
                            />
                            <Route
                              path="/ontology/*"
                              element={renderLayerManagedRoute(
                                <OntologyManagement />,
                                '온톨로지 관리 로딩 중...'
                              )}
                            />
                            {/* WT_008: explicit route for the Trino connector form
                            mount; placed before /data/* so it isn't shadowed. */}
                            <Route
                              path={DATA_ROUTES.trinoConnector}
                              element={renderLayerManagedRoute(
                                <TrinoConnectorPage />,
                                'Trino 커넥터 로딩 중...'
                              )}
                            />
                            <Route
                              path="/data/*"
                              element={renderLayerManagedRoute(
                                <DataManagement />,
                                '데이터 관리 로딩 중...'
                              )}
                            />
                            <Route
                              path="/sparql"
                              element={
                                <SafeComponent loadingText="SPARQL 쿼리 로딩 중...">
                                  <SparqlQuery />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/auto-insight/*"
                              element={renderLayerManagedRoute(
                                <AutoInsightPage />,
                                'Auto Insight 로딩 중...'
                              )}
                            />
                            <Route
                              path="/designer/secrets"
                              element={renderLayerManagedRoute(
                                <SecretManagementPage />,
                                'Secret Management 로딩 중...'
                              )}
                            />
                            <Route
                              path="/designer/*"
                              element={renderLayerManagedRoute(
                                <DesignerPage />,
                                'Designer 로딩 중...'
                              )}
                            />
                            <Route
                              path="/operate/*"
                              element={renderLayerManagedRoute(
                                <OperatePage />,
                                'Operate 로딩 중...'
                              )}
                            />
                            <Route
                              path="/settings"
                              element={
                                <SafeComponent loadingText="설정 로딩 중...">
                                  <SettingsPage />
                                </SafeComponent>
                              }
                            />
                            {/* VC-G7: design system showcase (인증 불필요) */}
                            <Route
                              path="/design-system"
                              element={
                                <SafeComponent loadingText="디자인 시스템 로딩 중...">
                                  <DesignSystemPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/settings/credential-vault"
                              element={
                                <SafeComponent loadingText="설정 로딩 중...">
                                  <SettingsPage />
                                </SafeComponent>
                              }
                            />
                            {/* Phase D (2026-06-07 / TASK_004): /settings/users
                              관리자 전용. RequireGlobalAdmin 가드가 비관리자를
                              `/projects` 로 redirect 하고 Snackbar 표시. */}
                            <Route
                              path="/settings/users"
                              element={
                                <RequireGlobalAdmin>
                                  <SafeComponent loadingText="사용자 승인 페이지 로딩 중...">
                                    <UserApprovalPage />
                                  </SafeComponent>
                                </RequireGlobalAdmin>
                              }
                            />
                            <Route
                              path="/objects/:objectType/:objectId/lineage"
                              element={renderLayerManagedRoute(
                                <ObjectLineageFullView />,
                                '전체 Lineage 로딩 중...'
                              )}
                            />
                            {/* VC-G6: Governance hub — Credential vault + Approval inbox + Loop evidence 통합 */}
                            <Route
                              path="/governance/hub"
                              element={
                                <SafeComponent loadingText="Governance Hub 로딩 중...">
                                  <GovernanceHubPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/governance/policies"
                              element={
                                <SafeComponent loadingText="Policy Console 로딩 중...">
                                  <PolicyConsole />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/governance/evidence"
                              element={
                                <SafeComponent loadingText="Evidence Browser 로딩 중...">
                                  <EvidenceBrowser />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/governance/observability"
                              element={
                                <SafeComponent loadingText="Observability Dashboard 로딩 중...">
                                  <ObservabilityDashboard />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/governance/journey"
                              element={
                                <SafeComponent loadingText="Journey Dashboard 로딩 중...">
                                  <JourneyRegressionDashboard />
                                </SafeComponent>
                              }
                            />
                            {/* 2026-05-31 IA Round 2 Wave 3: standalone /agent-console
                            라우트 폐기. Operate top-level은 /operate/* (OperatePage →
                            ConsoleSurface section="operate") 가 사이드바 + 패널을 모두
                            제공한다. agent-console menu key는 ConsolePanels에서
                            InferOpsAgent 패널로 마운트. Round 3 Wave R0 정리: 옛
                            pages/AgentConsole/ 폴더 전체 삭제 (외부 사용처 0건 확인). */}
                            <Route
                              path="/agent-marketplace"
                              element={
                                <SafeComponent loadingText="Agent Marketplace 로딩 중...">
                                  <AgentMarketplacePage />
                                </SafeComponent>
                              }
                            />
                            {/* RMPA Phase C — admin-only Ready Made Pack authoring.
                              Routes are gated client-side by the AdminGate
                              component inside each page (reads isGlobalAdmin
                              from AuthContext); backend additionally returns
                              403 for non-admin calls.
                              RMPA polish W6: nested under <AdminLayout /> so
                              every admin page renders inside the operator
                              shell (left rail + header with "콘솔 열기").
                              Phase D (2026-06-07 / TASK_004): top-level
                              `RequireGlobalAdmin` guard wraps the whole `/admin`
                              namespace so non-admins get redirected to
                              `/projects` with a Snackbar before AdminLayout
                              ever mounts. Page-level AdminGate stays as a
                              redundant in-component safety net. */}
                            <Route
                              path="/admin"
                              element={
                                <RequireGlobalAdmin>
                                  <SafeComponent loadingText="운영자 콘솔 로딩 중...">
                                    <AdminLayout />
                                  </SafeComponent>
                                </RequireGlobalAdmin>
                              }
                            >
                              <Route
                                path="ready-made-packs/authoring"
                                element={
                                  <SafeComponent loadingText="Ready Made Pack 작성 로딩 중...">
                                    <PackAuthoringPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="ready-made-packs/authoring/new"
                                element={
                                  <SafeComponent loadingText="Ready Made Pack 작성 로딩 중...">
                                    <PackAuthoringPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="ready-made-packs/authoring/:draftId"
                                element={
                                  <SafeComponent loadingText="Ready Made Pack 편집기 로딩 중...">
                                    <PackAuthoringEditorPage />
                                  </SafeComponent>
                                }
                              />
                            </Route>
                            {/* Phase 1 / P4 (2026-06-03): Org introduction routes.
                              Phase 1 / P6: gated behind VITE_ORG_UI_ENABLED.
                              When the flag is off, these routes redirect to
                              ``/projects`` so the staged rollout cohort sees
                              the personal-org-only surface. */}
                            <Route
                              path="/welcome"
                              element={
                                featureFlags.orgUiEnabled ? (
                                  <SafeComponent loadingText="Welcome 로딩 중...">
                                    <WelcomePage />
                                  </SafeComponent>
                                ) : (
                                  <Navigate to="/projects" replace />
                                )
                              }
                            />
                            <Route
                              path="/orgs/:slug/settings"
                              element={
                                featureFlags.orgUiEnabled ? (
                                  <SafeComponent loadingText="Organization 설정 로딩 중...">
                                    <OrgSettingsPage />
                                  </SafeComponent>
                                ) : (
                                  <Navigate to="/projects" replace />
                                )
                              }
                            />
                            <Route
                              path="/orgs/:slug/settings/:section"
                              element={
                                featureFlags.orgUiEnabled ? (
                                  <SafeComponent loadingText="Organization 설정 로딩 중...">
                                    <OrgSettingsPage />
                                  </SafeComponent>
                                ) : (
                                  <Navigate to="/projects" replace />
                                )
                              }
                            />
                            <Route
                              path="/projects/all"
                              element={
                                featureFlags.orgUiEnabled ? (
                                  <SafeComponent loadingText="내 모든 프로젝트 로딩 중...">
                                    <AllProjectsPage />
                                  </SafeComponent>
                                ) : (
                                  <Navigate to="/projects" replace />
                                )
                              }
                            />
                            {/* 2026-05-31 IA Round 3 Wave R4: SPA fallback이 unknown
                            path에 index.html을 200으로 반환하던 문제 해결.
                            모든 정의된 라우트에 매칭 안 되면 명시적 404. */}
                            <Route
                              path="*"
                              element={
                                <SafeComponent loadingText="페이지 확인 중...">
                                  <NotFoundPage />
                                </SafeComponent>
                              }
                            />
                          </Routes>
                        </AppLayout>
                      </SafeComponent>
                    </ToastProvider>
                  </AppThemeBridge>
                </ThemeModeProvider>
              </ProjectProvider>
            </OrgProvider>
          </Router>
        </AuthProvider>
      </I18nextProvider>
    </ErrorBoundary>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
